import { Flex, Spinner } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BiDonateBlood } from 'react-icons/bi';
import { FaCalendarCheck, FaImages } from 'react-icons/fa';
import { TfiWrite } from 'react-icons/tfi';
import { Link } from 'react-router-dom';
import { Context } from '../../context/Context';
import axiosInstance from '../../utils/axiosInstance';

const StatCard = ({ icon, label, count, color, to }) => (
    <Link to={to} className="flex-1 min-w-[200px]">
        <div className={`bg-white rounded-lg shadow-md p-6 flex items-center gap-4 hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 ${color}`}>
            <div className={`text-3xl ${color.replace('border-', 'text-')}`}>{icon}</div>
            <div>
                <p className="text-gray-500 text-sm font-medium">{label}</p>
                <p className="text-2xl font-bold text-gray-800">{count}</p>
            </div>
        </div>
    </Link>
);

const RecentItem = ({ item, type }) => (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-all">
        {item.img && (
            <img src={item.img} alt={item.title} className="w-12 h-12 rounded-md object-cover" />
        )}
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{item.title}</p>
            <p className="text-xs text-gray-500">{type}</p>
        </div>
    </div>
);

const Dashboard = () => {
    const {
        user,
        totalGalleryImages, setTotalGalleryImages,
        totalEvents, setTotalEvents,
        totalBlogs, setTotalBlogs,
        totalCauses, setTotalCauses,
    } = useContext(Context);

    const [loading, setLoading] = useState(true);
    const [recentBlogs, setRecentBlogs] = useState([]);
    const [recentEvents, setRecentEvents] = useState([]);
    const [recentCauses, setRecentCauses] = useState([]);
    const [recentImages, setRecentImages] = useState([]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);

                const [galleryRes, eventsRes, blogsRes, causesRes] = await Promise.allSettled([
                    axiosInstance.get('/api/gallery'),
                    axiosInstance.get('/api/events'),
                    axiosInstance.get('/api/blogs'),
                    axiosInstance.get('/api/cause'),
                ]);

                // Gallery
                if (galleryRes.status === 'fulfilled') {
                    const images = galleryRes.value.data.galleryImages || [];
                    setTotalGalleryImages(images.length);
                    localStorage.setItem('galleryImagesCount', images.length);
                    setRecentImages(images.slice(-4).reverse());
                }

                // Events
                if (eventsRes.status === 'fulfilled') {
                    const events = eventsRes.value.data.events || [];
                    setTotalEvents(events.length);
                    localStorage.setItem('totalEvents', events.length);
                    setRecentEvents(events.slice(-3).reverse());
                }

                // Blogs
                if (blogsRes.status === 'fulfilled') {
                    const blogs = blogsRes.value.data.blogs || [];
                    setTotalBlogs(blogs.length);
                    localStorage.setItem('totalBlogs', blogs.length);
                    setRecentBlogs(blogs.slice(-3).reverse());
                }

                // Causes
                if (causesRes.status === 'fulfilled') {
                    const causes = causesRes.value.data.causes || [];
                    setTotalCauses(causes.length);
                    localStorage.setItem('totalCauses', causes.length);
                    setRecentCauses(causes.slice(-3).reverse());
                }
            } catch (err) {
                console.error('Dashboard fetch error:', err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [setTotalGalleryImages, setTotalEvents, setTotalBlogs, setTotalCauses]);

    if (loading) {
        return (
            <Flex h="200px" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="green.500" />
            </Flex>
        );
    }

    return (
        <div className="scroller w-full h-full overflow-y-auto p-4 md:p-6">
            {/* Welcome Section */}
            <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                    Welcome back, <span className="text-green-600">{user?.name || 'Admin'}</span>
                </h1>
                <p className="text-gray-500 mt-1">Here's an overview of your website content</p>
            </div>

            {/* Stats Cards */}
            <div className="flex flex-wrap gap-4 mb-8">
                <StatCard
                    icon={<FaImages />}
                    label="Gallery Photos"
                    count={totalGalleryImages || 0}
                    color="border-orange-500"
                    to="/admin/gallery"
                />
                <StatCard
                    icon={<FaCalendarCheck />}
                    label="Events"
                    count={totalEvents || 0}
                    color="border-blue-500"
                    to="/admin/events"
                />
                <StatCard
                    icon={<TfiWrite />}
                    label="Blogs"
                    count={totalBlogs || 0}
                    color="border-purple-500"
                    to="/admin/blogs"
                />
                <StatCard
                    icon={<BiDonateBlood />}
                    label="Causes"
                    count={totalCauses || 0}
                    color="border-red-500"
                    to="/admin/cause"
                />
            </div>

            {/* Recent Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Recent Gallery */}
                <div className="bg-white rounded-lg shadow-md p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Recent Photos</h2>
                        <Link to="/admin/gallery" className="text-green-600 text-sm hover:underline">View All</Link>
                    </div>
                    {recentImages.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                            {recentImages.map((img, i) => (
                                <img key={img._id || i} src={img.img} alt="gallery" className="w-full h-24 object-cover rounded-md" />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-sm">No photos uploaded yet</p>
                    )}
                </div>

                {/* Recent Events */}
                <div className="bg-white rounded-lg shadow-md p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Recent Events</h2>
                        <Link to="/admin/events" className="text-green-600 text-sm hover:underline">View All</Link>
                    </div>
                    {recentEvents.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {recentEvents.map((event, i) => (
                                <RecentItem key={event._id || i} item={event} type={`${event.eventDate?.day || ''} ${event.eventDate?.month || ''} ${event.eventDate?.year || ''}`} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-sm">No events created yet</p>
                    )}
                </div>

                {/* Recent Blogs */}
                <div className="bg-white rounded-lg shadow-md p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Recent Blogs</h2>
                        <Link to="/admin/blogs" className="text-green-600 text-sm hover:underline">View All</Link>
                    </div>
                    {recentBlogs.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {recentBlogs.map((blog, i) => (
                                <RecentItem key={blog._id || i} item={blog} type={`${blog.date?.day || ''} ${blog.date?.month || ''} ${blog.date?.year || ''}`} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-sm">No blogs published yet</p>
                    )}
                </div>

                {/* Recent Causes */}
                <div className="bg-white rounded-lg shadow-md p-5">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Recent Causes</h2>
                        <Link to="/admin/cause" className="text-green-600 text-sm hover:underline">View All</Link>
                    </div>
                    {recentCauses.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {recentCauses.map((cause, i) => (
                                <RecentItem key={cause._id || i} item={cause} type="Cause" />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-sm">No causes added yet</p>
                    )}
                </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 bg-white rounded-lg shadow-md p-5">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
                <div className="flex flex-wrap gap-3">
                    <Link to="/admin/gallery">
                        <button className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium">
                            Upload Photos
                        </button>
                    </Link>
                    <Link to="/admin/events">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium">
                            Create Event
                        </button>
                    </Link>
                    <Link to="/admin/blogs">
                        <button className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors text-sm font-medium">
                            Write Blog
                        </button>
                    </Link>
                    <Link to="/admin/cause">
                        <button className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm font-medium">
                            Add Cause
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
